import axios from 'axios';
import { API_BASE, INDEXER_HEALTH_URL } from './config';

/**
 * Fetch health status of all blockchain indexers
 * @returns Health status information for all indexers
 */
export const fetchIndexerHealthStatus = async () => {
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    console.log('Fetching indexer health status');

    // Try to get health status from the API first
    try {
      const response = await axios.get(`${API_BASE}/indexer/health-status`, {
        headers: { 'Cache-Control': 'no-cache' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // If the API reports that the indexer process is not running, but we know it is,
      // try to get health status directly from the indexer process
      if (response.data?.status === 'error' &&
          response.data?.message?.includes('Indexer process is not running')) {
        console.warn('API reports indexer process is not running, trying direct connection');

        try {
          const fallbackResponse = await axios.get(`${INDEXER_HEALTH_URL}/health-status`, {
            headers: { 'Cache-Control': 'no-cache' },
            signal: controller.signal
          });

          return fallbackResponse.data;
        } catch (fallbackError: any) {
          console.error(`Direct health status request failed: ${fallbackError?.message || 'Unknown error'}`);
          return response.data;
        }
      }

      return response.data;
    } catch (apiError: any) {
      console.warn(`API health status request failed, falling back to indexer process: ${apiError?.message || 'Unknown error'}`);

      // If API fails, try to get health status directly from the indexer process
      const fallbackResponse = await axios.get(`${INDEXER_HEALTH_URL}/health-status`, {
        headers: { 'Cache-Control': 'no-cache' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return fallbackResponse.data;
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error('Indexer health status request timed out');
      // Return a default response instead of throwing
      return {
        status: 'error',
        message: 'Request timed out',
        overallStatus: 'unknown',
        timestamp: new Date().toISOString(),
        indexers: {}
      };
    }
    throw error;
  }
};

/**
 * Fetch health status of a specific blockchain indexer
 * @param blockchain Blockchain name (e.g., 'ethereum', 'bitcoin')
 * @returns Health status information for the specified indexer
 */
export const fetchBlockchainHealthStatus = async (blockchain: string) => {
  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    console.log(`Fetching health status for ${blockchain}`);

    // Try to get health status from the API first
    try {
      const response = await axios.get(`${API_BASE}/indexer/${blockchain}/health`, {
        headers: { 'Cache-Control': 'no-cache' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return response.data;
    } catch (apiError: any) {
      console.warn(`API health status request for ${blockchain} failed, falling back to indexer process: ${apiError?.message || 'Unknown error'}`);

      // If API fails, try to get health status directly from the indexer process
      const fallbackResponse = await axios.get(`${INDEXER_HEALTH_URL}/health/${blockchain}`, {
        headers: { 'Cache-Control': 'no-cache' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Convert the indexer process response to match the API response format
      return {
        status: fallbackResponse.data.status,
        blockchain: fallbackResponse.data.blockchain,
        healthStatus: fallbackResponse.data.healthStatus,
        timestamp: fallbackResponse.data.timestamp
      };
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error(`Health status request for ${blockchain} timed out`);
      // Return a default response instead of throwing
      return {
        status: 'error',
        message: 'Request timed out',
        blockchain,
        healthStatus: 'unknown',
        timestamp: new Date().toISOString()
      };
    }
    throw error;
  }
};
